import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface StorefrontCustomer {
  id: string;
  email: string | null;
  phone: string | null;
  given_name: string | null;
  family_name: string | null;
  default_location_id?: string | null;
  avatar_url?: string | null;
  job_title?: string | null;
  // Rich profile data
  birthday?: string | null;
  company_name?: string | null;
  member_since?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
  } | null;
  cards?: Array<{
    id: string;
    brand: string;
    last_4: string;
    exp_month: number;
    exp_year: number;
    cardholder_name?: string | null;
    billing_zip?: string | null;
  }>;
  badges?: string[];
}

interface LoyaltyInfo {
  account_id: string | null;
  balance: number;
  lifetime_points: number;
}

interface LoyaltyProgram {
  id: string;
  terminology: {
    one: string;
    other: string;
  };
  accrual_rules: Array<{
    accrual_type: string;
    points: number;
    spend_amount_money?: { amount: number };
  }>;
  reward_tiers: Array<{
    id: string;
    name: string;
    points: number;
    definition?: {
      discount_type?: string;
      percentage_discount?: string;
      fixed_discount_money?: { amount: number };
    };
  }>;
}

interface CustomerAuthContextType {
  customer: StorefrontCustomer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  businessId: string | null;
  isProfileComplete: boolean;
  showProfileCompletion: boolean;
  setShowProfileCompletion: (show: boolean) => void;

  // Auth actions
  requestOTP: (
    identifier: string,
    type: "email" | "phone",
    options?: {
      /**
       * Set when the OTP request originates from a referral portal. The
       * edge function uses it to white-label the OTP email with the
       * program's brand and to authorize against referral_partners
       * instead of storefront_customers.
       */
      referralProgramId?: string;
    },
  ) => Promise<{
    success: boolean;
    error?: string;
    isExistingCustomer?: boolean;
    existingCustomer?: {
      given_name: string | null;
      family_name: string | null;
      phone: string | null;
      email: string | null;
      cards_on_file?: Array<{ brand: string; last_4: string; exp_month: number; exp_year: number }>;
      member_since?: string | null;
    } | null;
  }>;
  verifyOTP: (otp: string, givenName?: string, familyName?: string) => Promise<{ success: boolean; error?: string; isNewCustomer?: boolean; isNewlyEnrolled?: boolean }>;
  silentLogin: (customerId: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  setBusinessId: (id: string) => void;
  updateProfile: (updates: Partial<StorefrontCustomer>) => Promise<void>;

  // Favorites
  favorites: string[];
  addFavorite: (productId: string) => Promise<void>;
  removeFavorite: (productId: string) => Promise<void>;
  isFavorite: (productId: string) => boolean;

  // Orders
  recentOrders: RecentOrder[];
  loadRecentOrders: () => Promise<void>;
  updateOrderStatus: (orderId: string, status: string, fulfillmentState: string | null) => void;

  // Loyalty
  loyaltyBalance: number | null;
  lifetimePoints: number | null;
  loyaltyAccountId: string | null;
  loyaltyProgram: LoyaltyProgram | null;
  isLoyaltyEnabled: boolean;
  refreshLoyalty: () => Promise<void>;

  // Data refresh
  lastFetched: number | null;
  refreshIfStale: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  isRefreshing: boolean;

  // Card management
  removeCard: (cardId: string) => void;
}

interface RecentOrder {
  id: string;
  total_cents: number;
  items: any[];
  created_at: string;
  status: string;
  order_type?: string | null;
  location_name?: string | null;
  customer_name?: string | null;
  fulfillment_state?: string | null;
  receipt_number?: string | null;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

const STORAGE_KEY_PREFIX = "customer_auth_";

/**
 * Read the HMAC-signed portal_token (issued by customer-verify-otp on
 * successful OTP verification) from localStorage for the given business.
 * Used by the referrals portal to authenticate calls to portal edge
 * functions. Returns null if not signed in for this business or if the
 * session pre-dates the portal_token rollout.
 */
export function getPortalTokenForBusiness(businessId: string | null | undefined): string | null {
  if (!businessId) return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${businessId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { portal_token?: string | null };
    return parsed.portal_token ?? null;
  } catch {
    return null;
  }
}

export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<StorefrontCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);

  // Loyalty state
  const [loyaltyBalance, setLoyaltyBalance] = useState<number | null>(null);
  const [lifetimePoints, setLifetimePoints] = useState<number | null>(null);
  const [loyaltyAccountId, setLoyaltyAccountId] = useState<string | null>(null);
  const [loyaltyProgram, setLoyaltyProgram] = useState<LoyaltyProgram | null>(null);

  // Data refresh state
  const [lastFetched, setLastFetched] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const STALE_THRESHOLD_MS = 60000; // 60 seconds

  // Pending OTP state
  const [pendingIdentifier, setPendingIdentifier] = useState<string | null>(null);
  const [pendingIdentifierType, setPendingIdentifierType] = useState<"email" | "phone" | null>(null);
  const [pendingExistingCustomer, setPendingExistingCustomer] = useState<{
    given_name: string | null;
    family_name: string | null;
    phone: string | null;
    email: string | null;
    cards_on_file?: Array<{ brand: string; last_4: string; exp_month: number; exp_year: number }>;
    member_since?: string | null;
  } | null>(null);

  // Check if profile is complete (has name)
  const isProfileComplete = !!(customer?.given_name);

  // Listen for service worker messages (push notification order updates)
  useEffect(() => {
    let isActive = true;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      console.log('[CustomerAuth] Service worker message received:', event.data);
      if (event.data?.type === 'ORDER_STATUS_UPDATE') {
        console.log('[CustomerAuth] Order update from push notification:', event.data);
        const { orderId, title } = event.data;

        // Quick optimistic update based on notification title
        if (orderId && isActive) {
          // Map notification titles to fulfillment states
          let newFulfillmentState: string | null = null;
          if (title?.includes('Ready')) newFulfillmentState = 'PREPARED';
          else if (title?.includes('Progress') || title?.includes('Preparing')) newFulfillmentState = 'RESERVED';
          else if (title?.includes('Complete')) newFulfillmentState = 'COMPLETED';
          else if (title?.includes('Canceled')) newFulfillmentState = 'CANCELED';

          if (newFulfillmentState) {
            // Immediate optimistic update to the specific order
            setRecentOrders(prev => prev.map(order =>
              order.id === orderId
                ? { ...order, fulfillment_state: newFulfillmentState }
                : order
            ));
            console.log('[CustomerAuth] Optimistically updated order', orderId, 'to', newFulfillmentState);
          }

          // Also do a full refresh to get complete data
          if (customer) {
            refreshCustomerData(customer.id);
          }
        }
      }
    };

    // Listen for messages from service worker
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    // Also ensure service worker is ready and log it
    navigator.serviceWorker?.ready.then(registration => {
      console.log('[CustomerAuth] Service worker ready, controller:', registration.active?.state);
    });

    return () => {
      isActive = false;
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [customer]);

  // Load session on mount when businessId is set
  useEffect(() => {
    if (!businessId) {
      setIsLoading(false);
      return;
    }

    const loadSession = async () => {
      setIsLoading(true); // Ensure loading state is true while checking session/fetching data
      const storageKey = `${STORAGE_KEY_PREFIX}${businessId}`;
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        try {
          const { customer: storedCustomer, token, loyalty } = JSON.parse(stored);
          // Decode token to check expiry
          const [, payload] = token.split(".");
          const decoded = JSON.parse(atob(payload));

          if (decoded.exp * 1000 > Date.now()) {
            setCustomer(storedCustomer);

            // Load cached loyalty info
            if (loyalty) {
              setLoyaltyBalance(loyalty.balance);
              setLifetimePoints(loyalty.lifetime_points);
              setLoyaltyAccountId(loyalty.account_id);
            }

            await loadFavorites(storedCustomer.id);
            await loadOrdersForCustomer(storedCustomer.id);

            // Refresh customer data from database
            await refreshCustomerData(storedCustomer.id);

            // Refresh loyalty in background
            refreshLoyaltyForCustomer(storedCustomer.id);
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch (e) {
          console.error("Error loading session:", e);
          localStorage.removeItem(storageKey);
        }
      }
      // Note: we intentionally do NOT proactively signOut a stale
      // Supabase auth session on mount. The auth-gate in Storefront
      // already blocks rendering until isAuthenticated (customer
      // session) is true, and verifyOTP's own pre-sign-out + magic-link
      // exchange is what fixes the cross-identity issue for the
      // submitted user. A mount-time signOut was racing with other
      // listeners (BusinessAuthContext, hooks on the dashboard route)
      // and hanging the Storefront skeleton.
      setIsLoading(false);
    };

    loadSession();
  }, [businessId]);

  // Refresh customer data directly from Supabase
  const refreshCustomerData = async (customerId: string) => {
    if (!businessId) return;

    try {
      const { data: freshCustomer, error } = await supabase
        .from("storefront_customers")
        .select("*")
        .eq("id", customerId)
        .eq("business_id", businessId)
        .single();

      if (error || !freshCustomer) {
        console.error("Error refreshing customer data:", error);
        return;
      }

      // Preserve local-only fields when updating
      const mergedCustomer = {
        ...freshCustomer,
        default_location_id: freshCustomer.default_location_id || customer?.default_location_id || null,
      };

      setCustomer(mergedCustomer);

      // Track when data was last fetched
      setLastFetched(Date.now());

      // Update stored session
      const storageKey = `${STORAGE_KEY_PREFIX}${businessId}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.customer = mergedCustomer;
        localStorage.setItem(storageKey, JSON.stringify(parsed));
      }
    } catch (error) {
      console.error("Error refreshing customer data:", error);
    }
  };

  const loadFavorites = async (customerId: string) => {
    console.log('[Favorites] Loading favorites for customer:', customerId);

    const { data, error } = await supabase
      .from("customer_favorites")
      .select("product_id")
      .eq("storefront_customer_id", customerId);

    console.log('[Favorites] Load result:', { data, error });

    if (!error && data) {
      setFavorites(data.map(f => f.product_id));
    } else if (error) {
      console.error('[Favorites] Load error:', error);
    }
  };

  // Orders are now loaded via refreshCustomerData
  const loadOrdersForCustomer = async (customerId: string) => {
    // Orders are fetched in refreshCustomerData
    // This function is kept for backwards compatibility but does nothing
    // The actual order loading happens in refreshCustomerData
  };

  // Loyalty is now refreshed via refreshCustomerData
  const refreshLoyaltyForCustomer = async (customerId: string) => {
    // Just call refreshCustomerData which fetches everything
    await refreshCustomerData(customerId);
  };

  const requestOTP = useCallback(async (
    identifier: string,
    type: "email" | "phone",
    options?: { referralProgramId?: string },
  ) => {
    if (!businessId) {
      return { success: false, error: "Business not loaded" };
    }

    // Normalize email identifiers to lowercase + trim regardless of
    // caller. Phone identifiers go through the edge function's own
    // digit-stripping path, so don't touch them here.
    const normalizedIdentifier =
      type === "email" ? identifier.trim().toLowerCase() : identifier.trim();

    try {
      const body: Record<string, unknown> = {
        identifier: normalizedIdentifier,
        identifier_type: type,
        business_id: businessId,
      };
      // Referral portal flow: tells the edge function to white-label
      // the OTP email with the program brand and authorize against
      // referral_partners instead of storefront_customers.
      if (options?.referralProgramId) {
        body.referral_program_id = options.referralProgramId;
      }
      const response = await supabase.functions.invoke("customer-request-otp", {
        body,
      });

      if (response.error) {
        // Edge function non-2xx: the real message is in the response context
        const ctx = response.error.context as Record<string, unknown> | undefined;
        const realMsg = (ctx?.error as string)
          || (ctx?.message as string)
          || response.data?.error
          || "User not found";
        return { success: false, error: realMsg };
      }

      if (response.data?.error) {
        return { success: false, error: response.data.error };
      }

      // Store the NORMALIZED identifier so verifyOTP looks up the same
      // form the OTP row was created against (the OTP table is keyed
      // off lowercased email).
      setPendingIdentifier(normalizedIdentifier);
      setPendingIdentifierType(type);

      // Store existing customer info if found
      const existingCustomer = response.data?.existingCustomer || null;
      setPendingExistingCustomer(existingCustomer);

      return {
        success: true,
        isExistingCustomer: response.data?.isExistingCustomer || false,
        existingCustomer: existingCustomer,
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }, [businessId]);

  const verifyOTP = useCallback(async (otp: string, givenName?: string, familyName?: string) => {
    if (!businessId || !pendingIdentifier || !pendingIdentifierType) {
      return { success: false, error: "No pending verification" };
    }

    try {
      const response = await supabase.functions.invoke("customer-verify-otp", {
        body: {
          identifier: pendingIdentifier,
          identifier_type: pendingIdentifierType,
          business_id: businessId,
          otp_code: otp,
          given_name: givenName,
          family_name: familyName,
        }
      });

      if (response.error) {
        // Edge function errors come in different formats depending on the error type
        // For 400 responses, the error message is in the context
        const errorMessage = response.error.context?.error
          || response.error.context?.message
          || response.data?.error
          || response.error.message
          || "Verification failed";
        return { success: false, error: errorMessage };
      }

      if (response.data?.error) {
        return { success: false, error: response.data.error };
      }

      const { customer: newCustomer, token, portal_token, supabase_session, loyalty, isNewCustomer, isNewlyEnrolled } = response.data;

      // ── Real Supabase Auth session swap ──
      // For business members + owners (anyone with an auth.users row),
      // the edge function returns a magic-link token_hash. Exchange it
      // here so subsequent supabase.from(...) queries run as the
      // OTP-verified user's auth.uid() — RLS then evaluates against
      // their actual user_visible_user_ids set. Without this step, the
      // storefront would keep operating on whatever Supabase session is
      // left over in the browser (anon, or a stale identity from the
      // dashboard at noknok.pro), and RLS would either reject every
      // query or — worse — return another user's records.
      //
      // For pure storefront customers (no auth.users row), the edge
      // function returns supabase_session=null; we stay on the
      // self-signed customer JWT path. Storefront-customer surfaces
      // don't go through PostgREST directly (they use service-role
      // edge functions), so the missing auth.uid() is fine there.
      if (supabase_session?.token_hash) {
        // First, sign out of any stale session so we don't carry over
        // a different identity (e.g. the dashboard owner's session).
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* noop */ }
        const { error: verifyErr } = await supabase.auth.verifyOtp({
          token_hash: supabase_session.token_hash,
          type: "magiclink",
        });
        if (verifyErr) {
          console.error("[CustomerAuth] verifyOtp(magiclink) failed:", verifyErr);
          return { success: false, error: "Sign-in succeeded but session sync failed. Try again." };
        }
      } else {
        // Pure storefront customer path — make sure we don't carry a
        // stale Supabase auth session into a non-member sign-in.
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* noop */ }
      }

      // Save session with loyalty info. portal_token is the HMAC-signed
      // companion token used by the referrals portal (and any future strong-
      // auth customer surfaces). The existing unsigned `token` is retained
      // for back-compat with the storefront's local expiry checks.
      const storageKey = `${STORAGE_KEY_PREFIX}${businessId}`;
      localStorage.setItem(
        storageKey,
        JSON.stringify({ customer: newCustomer, token, portal_token: portal_token ?? null, loyalty }),
      );

      setCustomer(newCustomer);
      setPendingIdentifier(null);
      setPendingIdentifierType(null);

      // Set loyalty info if available
      if (loyalty) {
        setLoyaltyBalance(loyalty.balance);
        setLifetimePoints(loyalty.lifetime_points);
        setLoyaltyAccountId(loyalty.account_id);
      }

      // Load favorites and orders
      await loadFavorites(newCustomer.id);
      await loadOrdersForCustomer(newCustomer.id);

      // Fetch the full rich profile (cards, address, badges, etc.)
      // This runs in the background to avoid blocking the login flow
      refreshCustomerData(newCustomer.id);

      // Show profile completion if new user or profile incomplete
      const profileComplete = !!(newCustomer.given_name);
      if (!profileComplete) {
        setShowProfileCompletion(true);
      }

      // Return the actual isNewCustomer from the edge function
      return { success: true, isNewCustomer: isNewCustomer, isNewlyEnrolled: isNewlyEnrolled };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }, [businessId, pendingIdentifier, pendingIdentifierType]);

  const updateProfile = useCallback(async (updates: Partial<StorefrontCustomer>) => {
    if (!customer || !businessId) return;

    // Route ALL updates through the edge function (uses service_role key).
    // Direct PostgREST updates are silently blocked by RLS because
    // storefront customers use custom OTP auth, not Supabase Auth,
    // so auth.uid() is null and the UPDATE policy never matches.
    const payload: Record<string, unknown> = {
      customer_id: customer.id,
      business_id: businessId,
    };

    for (const [key, value] of Object.entries(updates)) {
      payload[key] = value;
    }

    const { data, error } = await supabase.functions.invoke("profile-update", {
      body: payload,
    });

    if (error) {
      console.error("Profile update error:", error);
      throw new Error(error.message);
    }
    // Friendly business-rule failures (e.g. phone already in use) come back as
    // { error } with HTTP 200 so the message survives supabase-js.
    if ((data as { error?: string } | null)?.error) {
      throw new Error((data as { error: string }).error);
    }

    // Refresh customer data to get all updated fields
    await refreshCustomerData(customer.id);
  }, [customer, businessId]);

  const logout = useCallback(async () => {
    // Log logout event before clearing data
    if (businessId && customer) {
      try {
        await supabase.from("customer_events").insert({
          business_id: businessId,
          customer_id: customer.id,
          event_type: "logout",
          identifier: customer.email || customer.phone,
          identifier_type: customer.email ? "email" : "phone",
          metadata: {},
        });
      } catch (error) {
        console.error("Error logging logout event:", error);
      }
    }

    if (businessId) {
      const storageKey = `${STORAGE_KEY_PREFIX}${businessId}`;
      localStorage.removeItem(storageKey);
    }
    // Drop the real Supabase auth session too. For business members /
    // owners who signed in via OTP, verifyOTP set a real auth session;
    // logging out of customer auth without also clearing it would leave
    // a dangling Supabase session that subsequent visitors / OTP
    // sign-ins on the same browser would inherit.
    try { await supabase.auth.signOut({ scope: "local" }); } catch { /* noop */ }
    setCustomer(null);
    setFavorites([]);
    setRecentOrders([]);
    setLoyaltyBalance(null);
    setLifetimePoints(null);
    setLoyaltyAccountId(null);
    setShowProfileCompletion(false);
  }, [businessId, customer]);

  // Silent login - used after guest checkout to auto-sign in
  const silentLogin = useCallback(async (customerId: string) => {
    if (!businessId) {
      return { success: false, error: "Business not loaded" };
    }

    try {
      console.log("[SilentLogin] Auto-signing in customer:", customerId);

      // Fetch the customer record from our database
      const { data: customerData, error: customerError } = await supabase
        .from("storefront_customers")
        .select("id, email, phone, given_name, family_name, default_location_id")
        .eq("id", customerId)
        .eq("business_id", businessId)
        .single();

      if (customerError || !customerData) {
        console.error("[SilentLogin] Customer not found:", customerError);
        return { success: false, error: "Customer not found" };
      }

      // Create a simple session token (valid for 7 days)
      const expiry = Math.floor(Date.now() / 1000) + (7 * 24 * 60 * 60);
      const payload = { sub: customerId, exp: expiry };
      const token = `silent.${btoa(JSON.stringify(payload))}.checkout`;

      const newCustomer: StorefrontCustomer = {
        id: customerData.id,
        email: customerData.email,
        phone: customerData.phone,
        given_name: customerData.given_name,
        family_name: customerData.family_name,
        default_location_id: customerData.default_location_id,
      };

      // Save session to localStorage
      const storageKey = `${STORAGE_KEY_PREFIX}${businessId}`;
      localStorage.setItem(storageKey, JSON.stringify({ customer: newCustomer, token }));

      // Set customer state
      setCustomer(newCustomer);

      // Load favorites and refresh full profile in background
      await loadFavorites(customerId);
      refreshCustomerData(customerId);

      console.log("[SilentLogin] Auto-login successful");
      return { success: true };
    } catch (error: any) {
      console.error("[SilentLogin] Error:", error);
      return { success: false, error: error.message };
    }
  }, [businessId]);

  const addFavorite = useCallback(async (productId: string) => {
    if (!customer) return;

    console.log('[Favorites] Adding favorite:', { customerId: customer.id, productId });
    setFavorites(prev => [...prev, productId]);

    const { error } = await supabase.from("customer_favorites").insert({
      storefront_customer_id: customer.id,
      product_id: productId,
    });

    if (error) {
      console.error('[Favorites] Insert error:', error);
    } else {
      console.log('[Favorites] Insert successful');
    }
  }, [customer]);

  const removeFavorite = useCallback(async (productId: string) => {
    if (!customer) return;

    setFavorites(prev => prev.filter(id => id !== productId));

    await supabase
      .from("customer_favorites")
      .delete()
      .eq("storefront_customer_id", customer.id)
      .eq("product_id", productId);
  }, [customer]);

  const isFavorite = useCallback((productId: string) => {
    return favorites.includes(productId);
  }, [favorites]);

  const loadRecentOrders = useCallback(async () => {
    if (customer) {
      await refreshCustomerData(customer.id);
    }
  }, [customer, businessId]);

  // Update a single order's status without refetching (called from polling hook)
  const updateOrderStatus = useCallback((orderId: string, status: string, fulfillmentState: string | null) => {
    setRecentOrders(prev => prev.map(order =>
      order.id === orderId
        ? { ...order, status, fulfillment_state: fulfillmentState }
        : order
    ));
  }, []);

  const refreshLoyalty = useCallback(async () => {
    if (customer) {
      await refreshLoyaltyForCustomer(customer.id);
    }
  }, [customer, businessId]);

  // Show loyalty if customer has account OR if program data was fetched
  const isLoyaltyEnabled = loyaltyAccountId !== null || loyaltyProgram !== null;

  // Refresh data if stale (called when opening sheets)
  const refreshIfStale = useCallback(async () => {
    if (!customer) return;

    const isStale = !lastFetched || (Date.now() - lastFetched) > STALE_THRESHOLD_MS;
    if (isStale && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await refreshCustomerData(customer.id);
      } finally {
        setIsRefreshing(false);
      }
    }
  }, [customer, lastFetched, isRefreshing]);

  // Force refresh data (for manual refresh button)
  const forceRefresh = useCallback(async () => {
    if (!customer || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshCustomerData(customer.id);
    } finally {
      setIsRefreshing(false);
    }
  }, [customer, isRefreshing]);

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        isAuthenticated: !!customer,
        isLoading,
        businessId,
        isProfileComplete,
        showProfileCompletion,
        setShowProfileCompletion,
        requestOTP,
        verifyOTP,
        silentLogin,
        logout,
        setBusinessId,
        updateProfile,
        favorites,
        addFavorite,
        removeFavorite,
        isFavorite,
        recentOrders,
        loadRecentOrders,
        updateOrderStatus,
        loyaltyBalance,
        lifetimePoints,
        loyaltyAccountId,
        loyaltyProgram,
        isLoyaltyEnabled,
        refreshLoyalty,
        lastFetched,
        refreshIfStale,
        forceRefresh,
        isRefreshing,
        removeCard: (cardId: string) => {
          if (customer) {
            setCustomer({
              ...customer,
              cards: customer.cards?.filter(card => card.id !== cardId) || []
            });
          }
        },
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error("useCustomerAuth must be used within a CustomerAuthProvider");
  }
  return context;
};
